'use client';

import InlineStyle from '@/components/InlineStyle';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookDemoButton } from '@/components/ui';
import BookDemoSection from '@/components/BookDemoSection';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import { slideInLeft, slideInRight, scaleIn, viewportOnce, spring, springSoft } from '@/lib/motion';
import { useTranslation } from '@/hooks/useTranslation';
import {
  TrendingUp,
  AlertTriangle,
  BarChart3,
  MonitorDot,
  Users,
  Sparkles,
  Send,
  BellRing,
  Tags,
  QrCode,
  Activity,
  RefreshCcw,
  Check,
} from 'lucide-react';

const IMG = '/images/location-intelligence';

/* ── Brand tints (sky-blue #0098FF family) ───────────────────────────────── */
const TINTS = {
  blue:   { bg: '#EAF6FF', stroke: '#BFE3FF', accent: '#0098FF' },
  sky:    { bg: '#EAF3FF', stroke: '#C7DEFF', accent: '#2E90FF' },
  violet: { bg: '#E9EFFF', stroke: '#C6D6FF', accent: '#0057D4' },
};

/* ── Content ─────────────────────────────────────────────────────────────── */
const HERO = {
  titleA: 'Run a thousand locations',
  titleB: 'like your best one',
  body: 'Monitor reviews, customer feedback, local reputation, and store performance across every location from a single platform.',
};

const HERO_SLIDES = [`${IMG}/hero-map.png`, `${IMG}/hero-map-2.png`];

const TEAM_SLIDES = [`${IMG}/quadrant-chart.png`, `${IMG}/team-map.png`, `${IMG}/team-publish.png`];

const IMPACT = [
  { value: '10,000+', label: 'Locations monitored in real time' },
  { value: '5×',      label: 'Higher engagement with location-tagged responses' },
  { value: '65%',     label: 'Reduction in negative sentiment per outlet' },
  { value: '60%',     label: 'Faster local team response times' },
];

const CAPABILITIES = [
  { Icon: TrendingUp,    Mini: 'table', title: 'Know which locations are thriving', desc: 'Track ratings, feedback trends, customer sentiment, and reputation scores across every store.', tint: 'blue' },
  { Icon: AlertTriangle, Mini: 'alert', title: 'Spot issues before they spread',    desc: 'Detect recurring complaints, service gaps, and operational issues before they impact your brand.', tint: 'violet' },
  { Icon: BarChart3,     Mini: 'bars',  title: 'Benchmark every store',             desc: 'Compare locations, regions, and franchise partners against your top-performing outlets.', tint: 'sky' },
];

const TEAM_FEATURES = [
  { Icon: MonitorDot, title: 'Centralized Monitoring',        desc: 'Track reputation, feedback, and performance across every location.' },
  { Icon: Users,      title: 'Role-Based Access',             desc: 'Give regional managers, franchise owners, and store teams relevant insights.' },
  { Icon: Sparkles,   title: 'AI-Powered Insights',          desc: 'Predict potential challenges with AI-powered forecasting.' },
  { Icon: Send,       title: 'Publishing & Brand Approval',   desc: 'Publish and personalize content across all locations while enforcing brand guidelines and allowing local customization.' },
  { Icon: BellRing,   title: 'Automated Alerts',             desc: 'Get notified when ratings drop, complaints spike, or sentiment shifts.' },
  { Icon: Tags,       title: 'Dynamic Conversation Tagging',  desc: 'Aggregate unstructured customer conversations and reviews by mapping keywords to the correct location.' },
];

const FEEDBACK = [
  { Icon: QrCode,    Mini: 'qr',     title: 'Omnichannel QR Surveys', desc: 'Capture feedback wherever customers engage — from receipts and menus to mirrors and in-store signage. One scan is all it takes.' },
  { Icon: Activity,  Mini: 'chart',  title: 'Real-time Insights',     desc: 'Gather feedback the moment an experience ends, while details are fresh and customers are still engaged.' },
  { Icon: RefreshCcw, Mini: 'loop',  title: 'Close the Feedback Loop', desc: 'Respond instantly, recover at-risk experiences, and turn customer feedback into lasting loyalty before they leave.' },
];

const INDUSTRIES = [
  {
    name: 'Automotive',
    img: `${IMG}/ind-automotive.png`,
    headBg: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
    challenge: 'Inconsistent dealership experiences creating brand perception gaps.',
    solution: 'SLA-driven CX with GenAI automation across every dealership.',
    metrics: [
      { label: 'Response time', value: '-87%', pct: 87 },
      { label: 'Customer Satisfaction', value: '+32%', pct: 32 },
    ],
    quote: 'Transformed our dealership feedback into actionable intelligence.',
  },
  {
    name: 'F&B',
    img: `${IMG}/ind-fnb.png`,
    headBg: 'linear-gradient(135deg, #EAF3FF 0%, #EAF6FF 100%)',
    challenge: 'Fragmented customer feedback with no clear path to retention.',
    solution: 'QR feedback plus integrated loyalty workflows across locations.',
    metrics: [
      { label: 'Repeat visits', value: '+45%', pct: 45 },
      { label: 'Feedback volume', value: '+215%', pct: 95 },
    ],
    quote: 'Connected our store-level experience to our global brand strategy.',
  },
  {
    name: 'Beauty',
    img: `${IMG}/ind-beauty.png`,
    headBg: 'linear-gradient(135deg, #FDECF2 0%, #FCE4EC 100%)',
    challenge: 'Salon customers sharing feedback across platforms with no central visibility.',
    solution: 'Hyper-local engagement with central oversight across every salon.',
    metrics: [
      { label: 'NPS', value: '+22 pts', pct: 60 },
      { label: 'Service recovery', value: '94%', pct: 94 },
    ],
    quote: 'Transformed our salon network into a connected experience ecosystem.',
  },
];

const ROLE_CHECKS = [
  'Single view of every location',
  'Instant issue detection',
  'Regional performance comparisons',
  'Faster action and accountability',
];

/* ── Brand channel icons ──────────────────────────────────────────────────── */
function ChannelBadge({ label, children }) {
  return (
    <span title={label} aria-label={label} style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff', border: '1px solid var(--border-light)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {children}
    </span>
  );
}

const CHANNELS = (
  <>
    <ChannelBadge label="Google">
      <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
        <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
        <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
        <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
      </svg>
    </ChannelBadge>
    <ChannelBadge label="Facebook">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" /></svg>
    </ChannelBadge>
    <ChannelBadge label="Instagram">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E4405F" strokeWidth="2" aria-hidden="true">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" strokeLinecap="round" />
      </svg>
    </ChannelBadge>
    <ChannelBadge label="LinkedIn">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
        <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z" />
        <rect x="2" y="9" width="4" height="12" />
        <circle cx="4" cy="4" r="2" />
      </svg>
    </ChannelBadge>
    <ChannelBadge label="X">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="#000" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
    </ChannelBadge>
    <ChannelBadge label="YouTube">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="#FF0000" aria-hidden="true"><path d="M23 12s0-3.6-.46-5.32a2.51 2.51 0 00-1.77-1.77C19.05 4.45 12 4.45 12 4.45s-7.05 0-8.77.46A2.51 2.51 0 001.46 6.68C1 8.4 1 12 1 12s0 3.6.46 5.32a2.51 2.51 0 001.77 1.77C4.95 19.55 12 19.55 12 19.55s7.05 0 8.77-.46a2.51 2.51 0 001.77-1.77C23 15.6 23 12 23 12zM9.75 15.27V8.73L15.5 12z" /></svg>
    </ChannelBadge>
  </>
);

/* ── Reusable bits ───────────────────────────────────────────────────────── */
function IconTile({ Icon, tint = 'blue', size = 'md' }) {
  const T = TINTS[tint];
  const dim = size === 'lg' ? 56 : size === 'md' ? 46 : 40;
  const iconSize = size === 'lg' ? 26 : 20;
  return (
    <div style={{ width: dim, height: dim, borderRadius: 14, background: T.bg, border: `1px solid ${T.stroke}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={iconSize} color={T.accent} strokeWidth={1.8} />
    </div>
  );
}

/* ── Mini UI mockups (capability + feedback cards) ───────────────────────── */
function MiniTable({ accent }) {
  const rows = [4.7, 4.5, 4.8, 4.2, 4.6];
  return (
    <svg viewBox="0 0 260 150" style={{ width: '100%', height: 'auto', display: 'block' }} aria-hidden="true">
      <rect x="0" y="0" width="260" height="150" rx="10" fill="#fff" stroke={accent} strokeOpacity="0.18" />
      <text x="16" y="24" fontSize="9" fontWeight="700" fill={accent}>Location Profiles Summary</text>
      <text x="150" y="24" fontSize="8" fill="#94a3b8">Rating</text>
      <text x="210" y="24" fontSize="8" fill="#94a3b8">Mentions</text>
      <line x1="16" y1="32" x2="244" y2="32" stroke={accent} strokeOpacity="0.15" />
      {rows.map((r, i) => {
        const y = 48 + i * 19;
        return (
          <g key={i}>
            <rect x="16" y={y - 9} width="58" height="6" rx="3" fill={accent} fillOpacity={0.35} />
            {[0, 1, 2, 3, 4].map((s) => (
              <circle key={s} cx={150 + s * 9} cy={y - 6} r="2.4" fill={s < Math.round(r) ? '#F5A623' : '#e2e8f0'} />
            ))}
            <rect x="214" y={y - 9} width="22" height="6" rx="3" fill={accent} fillOpacity={0.55} />
            {i < rows.length - 1 && <line x1="16" y1={y + 5} x2="244" y2={y + 5} stroke="#eef2f7" />}
          </g>
        );
      })}
    </svg>
  );
}

function MiniAlert({ accent }) {
  return (
    <svg viewBox="0 0 260 150" style={{ width: '100%', height: 'auto', display: 'block' }} aria-hidden="true">
      <rect x="0" y="0" width="260" height="150" rx="10" fill="#fff" stroke={accent} strokeOpacity="0.18" />
      <rect x="20" y="26" width="220" height="98" rx="10" fill="#FFF7ED" stroke="#FED7AA" />
      <circle cx="44" cy="52" r="13" fill="#FB923C" fillOpacity="0.18" />
      <path d="M44 45 L50 56 H38 Z" fill="#EA580C" />
      <rect x="40.5" y="50" width="7" height="3.5" rx="1.75" fill="#fff" />
      <circle cx="44" cy="55" r="1.4" fill="#fff" />
      <text x="66" y="50" fontSize="10" fontWeight="700" fill="#B45309">Location Alert</text>
      <rect x="66" y="58" width="150" height="5" rx="2.5" fill="#C2853B" fillOpacity="0.5" />
      <rect x="66" y="68" width="120" height="5" rx="2.5" fill="#C2853B" fillOpacity="0.3" />
      <rect x="20" y="98" width="120" height="6" rx="3" fill={accent} fillOpacity="0.45" />
      <rect x="172" y="94" width="48" height="16" rx="8" fill={accent} fillOpacity="0.9" />
      <rect x="184" y="100" width="24" height="4" rx="2" fill="#fff" />
    </svg>
  );
}

function MiniBars({ accent }) {
  const zones = [
    { x: 28,  bars: [34, 52, 40] },
    { x: 92,  bars: [50, 70, 44] },
    { x: 156, bars: [40, 58, 66] },
    { x: 220, bars: [62, 48, 56] },
  ];
  const labels = ['North', 'South', 'East', 'West'];
  return (
    <svg viewBox="0 0 260 150" style={{ width: '100%', height: 'auto', display: 'block' }} aria-hidden="true">
      <rect x="0" y="0" width="260" height="150" rx="10" fill="#fff" stroke={accent} strokeOpacity="0.18" />
      <text x="16" y="22" fontSize="9" fontWeight="700" fill={accent}>Area Wise Sentiment Analysis</text>
      <line x1="16" y1="116" x2="244" y2="116" stroke={accent} strokeOpacity="0.18" />
      {zones.map((z, zi) => (
        <g key={zi}>
          {z.bars.map((h, bi) => (
            <rect key={bi} x={z.x - 18 + bi * 12} y={116 - h} width="9" height={h} rx="2"
              fill={bi === 0 ? '#22C55E' : bi === 1 ? accent : '#F59E0B'} fillOpacity="0.85" />
          ))}
          <text x={z.x} y="130" fontSize="7" fill="#94a3b8" textAnchor="middle">{labels[zi]}</text>
        </g>
      ))}
    </svg>
  );
}

function MiniQR({ accent }) {
  return (
    <svg viewBox="0 0 260 150" style={{ width: '100%', height: 'auto', display: 'block' }} aria-hidden="true">
      <rect x="0" y="0" width="260" height="150" rx="10" fill="#0F1F3D" />
      <rect x="92" y="24" width="76" height="76" rx="10" fill="#fff" />
      {/* simple qr blocks */}
      <g fill="#0F1F3D">
        <rect x="100" y="32" width="18" height="18" /><rect x="142" y="32" width="18" height="18" /><rect x="100" y="74" width="18" height="18" />
        <rect x="104" y="36" width="10" height="10" fill="#fff" /><rect x="146" y="36" width="10" height="10" fill="#fff" /><rect x="104" y="78" width="10" height="10" fill="#fff" />
        <rect x="126" y="34" width="5" height="5" /><rect x="134" y="42" width="5" height="5" /><rect x="126" y="50" width="5" height="5" />
        <rect x="142" y="60" width="5" height="5" /><rect x="150" y="66" width="5" height="5" /><rect x="126" y="74" width="5" height="5" />
        <rect x="138" y="82" width="5" height="5" /><rect x="150" y="84" width="5" height="5" /><rect x="126" y="90" width="5" height="5" />
      </g>
      <rect x="78" y="116" width="104" height="8" rx="4" fill={accent} fillOpacity="0.85" />
      <rect x="98" y="130" width="64" height="6" rx="3" fill="#fff" fillOpacity="0.25" />
    </svg>
  );
}

function MiniChart({ accent }) {
  const bars = [22, 38, 30, 52, 44, 64, 58];
  return (
    <svg viewBox="0 0 260 150" style={{ width: '100%', height: 'auto', display: 'block' }} aria-hidden="true">
      <rect x="0" y="0" width="260" height="150" rx="10" fill="#0F1F3D" />
      <rect x="16" y="18" width="90" height="6" rx="3" fill="#fff" fillOpacity="0.6" />
      <line x1="16" y1="116" x2="244" y2="116" stroke="#fff" strokeOpacity="0.15" />
      {bars.map((h, i) => (
        <rect key={i} x={26 + i * 31} y={116 - h} width="18" height={h} rx="3" fill={accent} fillOpacity={0.5 + i * 0.06} />
      ))}
      <polyline points="35,80 66,66 97,72 128,50 159,58 190,38 221,46" fill="none" stroke="#fff" strokeOpacity="0.7" strokeWidth="2" />
    </svg>
  );
}

function MiniLoop({ accent }) {
  return (
    <svg viewBox="0 0 260 150" style={{ width: '100%', height: 'auto', display: 'block' }} aria-hidden="true">
      <rect x="0" y="0" width="260" height="150" rx="10" fill="#0F1F3D" />
      <rect x="36" y="22" width="188" height="106" rx="10" fill="#fff" />
      <rect x="52" y="36" width="120" height="7" rx="3.5" fill="#0F1F3D" fillOpacity="0.7" />
      <rect x="52" y="50" width="156" height="5" rx="2.5" fill="#0F1F3D" fillOpacity="0.2" />
      <g>
        {[0, 1, 2, 3, 4].map((i) => (
          <circle key={i} cx={64 + i * 22} cy="78" r="9" fill={i < 4 ? '#F5A623' : '#e2e8f0'} />
        ))}
      </g>
      <rect x="52" y="98" width="86" height="16" rx="8" fill={accent} />
      <rect x="68" y="104" width="54" height="4" rx="2" fill="#fff" />
    </svg>
  );
}

const MINIS = { table: MiniTable, alert: MiniAlert, bars: MiniBars, qr: MiniQR, chart: MiniChart, loop: MiniLoop };

/* ── Page ─────────────────────────────────────────────────────────────────── */
export default function LocationIntelligencePage() {
  const { t } = useTranslation();
  const [heroSlide, setHeroSlide] = useState(0);
  const [teamSlide, setTeamSlide] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHeroSlide((i) => (i + 1) % HERO_SLIDES.length), 2800);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setTeamSlide((i) => (i + 1) % TEAM_SLIDES.length), 3200);
    return () => clearInterval(id);
  }, []);
  return (
    <>
      <InlineStyle css={`
        .li-wrap { max-width: var(--max-content, 1200px); margin: 0 auto; padding: 0 24px; }
        .li-eyebrow { font-size: 12px; font-weight: 800; color: var(--brand); letter-spacing: 1.8px; text-transform: uppercase; }
        .li-hero { padding: clamp(92px, 9vh, 116px) 0 clamp(44px, 5.5vh, 72px); }
        .li-hero-grid { display: grid; grid-template-columns: 1.02fr 0.98fr; gap: clamp(32px, 4.5vw, 64px); align-items: center; }
        .li-hero-stage { position: relative; width: 100%; aspect-ratio: 1226 / 749; }
        .li-hero-slide { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; opacity: 0; transition: opacity 0.8s ease; filter: drop-shadow(0 20px 50px rgba(0,87,212,0.18)); }
        .li-hero-slide.is-active { opacity: 1; }
        @media (prefers-reduced-motion: reduce) { .li-hero-slide { transition: opacity 0.3s ease; } }
        .li-cap-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 24px; }
        .li-team-grid { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(36px, 4.5vw, 64px); align-items: center; }
        .li-team-cards { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 16px; }
        .li-team-card { display: flex; gap: 13px; align-items: flex-start; height: 100%; background: #fff; border: 1px solid var(--border-light); border-radius: var(--radius-2xl); padding: 18px 16px; transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease; }
        .li-team-card:hover { transform: translateY(-3px); border-color: #BFE3FF; box-shadow: 0 10px 28px rgba(0,87,212,0.10); }
        .li-team-stage { position: relative; width: 100%; aspect-ratio: 1231 / 883; border-radius: var(--radius-2xl); overflow: hidden; box-shadow: 0 20px 50px rgba(15,31,61,0.22); background: #0F1F3D; }
        .li-team-slide { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0; transition: opacity 0.8s ease; }
        .li-team-slide.is-active { opacity: 1; }
        @media (prefers-reduced-motion: reduce) { .li-team-slide { transition: opacity 0.3s ease; } }
        .li-feedback-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 24px; }
        .li-one-grid { display: grid; grid-template-columns: 0.85fr 1.15fr; gap: 48px; align-items: center; }
        .li-ind-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 24px; }
        .li-role-grid { display: grid; grid-template-columns: 0.9fr 1.1fr; gap: 48px; align-items: center; }
        .li-role-checks { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 12px 24px; }
        @media (max-width: 920px) {
          .li-hero-grid, .li-team-grid, .li-one-grid, .li-role-grid { grid-template-columns: 1fr; }
          .li-cap-grid, .li-feedback-grid, .li-ind-grid { grid-template-columns: 1fr; }
          .li-team-cards { grid-template-columns: repeat(2, minmax(0,1fr)); }
          .li-team-stage { margin: 0 auto; }
          .li-one-media { order: -1; }
        }
        @media (max-width: 560px) {
          .li-team-cards, .li-role-checks { grid-template-columns: 1fr; }
        }
      `} />

      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section className="li-hero" style={{ background: 'var(--gradient-hero)', position: 'relative', overflow: 'hidden' }}>
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
          <span style={{ position: 'absolute', width: 660, height: 660, top: -240, left: '4%', borderRadius: '50%', filter: 'blur(80px)', background: 'radial-gradient(circle at 40% 40%, rgba(0,152,255,0.20), transparent 70%)' }} />
          <span style={{ position: 'absolute', width: 520, height: 520, top: -120, right: '2%', borderRadius: '50%', filter: 'blur(80px)', background: 'radial-gradient(circle at 60% 30%, rgba(0,87,212,0.14), transparent 70%)' }} />
        </div>
        <div className="li-wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="li-hero-grid">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }}
                style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'var(--type-hero-title)', color: 'var(--text-heading)', lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 18px' }}
              >
                {t(HERO.titleA)} <span className="serif-accent italic">{t(HERO.titleB)}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.12 }}
                style={{ fontSize: 'clamp(16px, 1.4vw, 18px)', color: 'var(--text-body)', lineHeight: 1.7, margin: '0 0 28px', maxWidth: 480 }}
              >
                {t(HERO.body)}
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.2 }}>
                <BookDemoButton label={t('Book a demo')} />
              </motion.div>
            </div>
            <motion.div variants={scaleIn} initial="hidden" animate="show">
              <div className="li-hero-stage" aria-hidden="true">
                {HERO_SLIDES.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt={i === 0 ? t('Location intelligence map view') : ''}
                    className={`li-hero-slide ${i === heroSlide ? 'is-active' : ''}`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable={false}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── IMPACT BAND ────────────────────────────────────────────────── */}
      <section style={{ padding: '0 0 var(--section-pad-y)', marginTop: 'calc(var(--section-pad-y) * -0.6)', position: 'relative', zIndex: 2 }}>
        <div className="li-wrap">
          <MetricsBand items={IMPACT} columns={4} />
        </div>
      </section>

      {/* ── PLATFORM CAPABILITIES ──────────────────────────────────────── */}
      <section style={{ padding: 'var(--section-pad)' }}>
        <div className="li-wrap">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ marginBottom: 44, maxWidth: 760 }}>
            <div className="li-eyebrow" style={{ marginBottom: 12 }}>{t('Platform Capabilities')}</div>
            <h2 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(28px, 3.4vw, 40px)', color: 'var(--text-heading)', lineHeight: 1.12, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
              {t('Turn local signals into')} <span className="serif-accent italic">{t('business intelligence')}</span>
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', lineHeight: 1.7, margin: 0 }}>
              {t('Every review, rating, survey response, and customer interaction contains valuable insight. Locobuzz helps you uncover patterns, identify risks, and make smarter decisions across every location.')}
            </p>
          </motion.div>

          <div className="li-cap-grid">
            {CAPABILITIES.map((c, i) => {
              const T = TINTS[c.tint];
              const Mini = MINIS[c.Mini];
              return (
                <motion.div key={c.title} className="lb-card" initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ ...spring, delay: i * 0.08 }} style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
                  <div style={{ background: T.bg, padding: 18, borderBottom: `1px solid ${T.stroke}` }}>
                    <Mini accent={T.accent} />
                  </div>
                  <div style={{ padding: '20px 24px 24px' }}>
                    <IconTile Icon={c.Icon} tint={c.tint} size="sm" />
                    <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 18, color: 'var(--text-heading)', lineHeight: 1.3, margin: '14px 0 8px' }}>{t(c.title)}</h3>
                    <p style={{ fontSize: 14.5, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{t(c.desc)}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── BUILT FOR MULTI-LOCATION TEAMS ─────────────────────────────── */}
      <section style={{ padding: 'var(--section-pad)', background: 'var(--surface-page)' }}>
        <div className="li-wrap">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ maxWidth: 760, marginBottom: 'clamp(32px, 4vw, 48px)' }}>
            <h2 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(26px, 3vw, 36px)', color: 'var(--text-heading)', lineHeight: 1.14, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
              {t('Built for how multi-location teams')} <span className="serif-accent italic">{t('actually work')}</span>
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', lineHeight: 1.7, margin: 0 }}>
              {t('Whether you manage 10 stores or 10,000, give every team the visibility they need to act faster and stay aligned.')}
            </p>
          </motion.div>

          <div className="li-team-grid">
            <div className="li-team-cards">
              {TEAM_FEATURES.map((f, i) => (
                <motion.div key={f.title} className="li-team-card" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ ...spring, delay: (i % 2) * 0.06 }}>
                  <IconTile Icon={f.Icon} tint="blue" size="sm" />
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: 'var(--text-heading)', margin: '2px 0 5px', lineHeight: 1.25 }}>{t(f.title)}</h3>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55, margin: 0 }}>{t(f.desc)}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div variants={slideInRight} initial="hidden" whileInView="show" viewport={viewportOnce}>
              <div className="li-team-stage" aria-hidden="true">
                {TEAM_SLIDES.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    className={`li-team-slide ${i === teamSlide ? 'is-active' : ''}`}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── MAKE FEEDBACK YOUR GROWTH ENGINE (dark) ────────────────────── */}
      <section style={{ padding: 'var(--section-pad)', background: 'linear-gradient(160deg, #0B1A36 0%, #122A52 100%)', position: 'relative', overflow: 'hidden' }}>
        <div className="li-wrap" style={{ position: 'relative' }}>
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 44, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
            <h2 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(26px, 3.2vw, 38px)', color: '#fff', lineHeight: 1.14, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
              {t('Make feedback your')} <span className="serif-accent italic" style={{ color: '#7FC4FF' }}>{t('growth engine')}</span>
            </h2>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.78)', lineHeight: 1.7, margin: 0 }}>
              {t('Capture sentiment at the moment it happens — right where the experience occurs — and close the loop before a customer walks away unhappy.')}
            </p>
          </motion.div>

          <div className="li-feedback-grid">
            {FEEDBACK.map((f, i) => {
              const Mini = MINIS[f.Mini];
              return (
                <motion.div key={f.title} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ ...spring, delay: i * 0.08 }}
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 'var(--radius-2xl)', padding: 20, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 18 }}>
                    <Mini accent="#2E90FF" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <f.Icon size={20} color="#7FC4FF" strokeWidth={1.9} />
                    <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: '#7FC4FF', margin: 0 }}>{t(f.title)}</h3>
                  </div>
                  <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.65, margin: 0 }}>{t(f.desc)}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── EVERYTHING IN ONE PLACE ────────────────────────────────────── */}
      <section style={{ padding: 'var(--section-pad)' }}>
        <div className="li-wrap">
          <div className="li-one-grid">
            <motion.div variants={slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce}>
              <h2 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(26px, 3vw, 38px)', color: 'var(--text-heading)', lineHeight: 1.14, letterSpacing: '-0.02em', margin: '0 0 16px' }}>
                {t('Everything a multi-location team needs, in one place')}
              </h2>
              <p style={{ fontSize: 16, color: 'var(--text-body)', lineHeight: 1.7, margin: '0 0 28px', maxWidth: 440 }}>
                {t('From storefronts to branches and clinics — connect the channels your customers actually use, and bring every conversation back to the right location.')}
              </p>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 }}>{t('Channels Covered')}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>{CHANNELS}</div>
            </motion.div>
            <motion.div className="li-one-media" variants={slideInRight} initial="hidden" whileInView="show" viewport={viewportOnce}
              style={{ borderRadius: 'var(--radius-3xl)', overflow: 'hidden', border: '1px solid var(--border-light)', boxShadow: '0 20px 50px rgba(0,87,212,0.12)', background: '#0B1A36' }}>
              <video src={`${IMG}/product-demo.mp4`} autoPlay muted loop playsInline preload="metadata" style={{ width: '100%', height: 'auto', display: 'block' }} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── TRUSTED ACROSS INDUSTRIES ──────────────────────────────────── */}
      <section style={{ padding: 'var(--section-pad)', background: 'var(--surface-page)' }}>
        <div className="li-wrap">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 44, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            <h2 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(26px, 3.2vw, 38px)', color: 'var(--text-heading)', lineHeight: 1.14, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
              {t('Trusted across')} <span className="serif-accent italic">{t('industries')}</span>
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', lineHeight: 1.7, margin: 0 }}>
              {t('Same platform, same playbook — adapted to how each business actually runs on the ground.')}
            </p>
          </motion.div>

          <div className="li-ind-grid">
            {INDUSTRIES.map((ind, i) => (
              <motion.div key={ind.name} className="lb-card" initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ ...spring, delay: i * 0.1 }}
                style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
                <div style={{ background: ind.headBg, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 22, color: 'var(--text-heading)' }}>{t(ind.name)}</span>
                  <img src={ind.img} alt={t(ind.name)} loading="lazy" decoding="async" style={{ width: 72, height: 72, objectFit: 'contain', flexShrink: 0 }} />
                </div>
                <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--brand)', marginBottom: 4 }}>{t('Challenge')}</div>
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>{t(ind.challenge)}</p>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--brand)', marginBottom: 4 }}>{t('Solution')}</div>
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>{t(ind.solution)}</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
                    {ind.metrics.map((m) => (
                      <div key={m.label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t(m.label)}</span>
                          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-heading)' }}>{m.value}</span>
                        </div>
                        <div style={{ height: 5, borderRadius: 999, background: 'var(--border-light)', overflow: 'hidden' }}>
                          <div style={{ width: `${m.pct}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, #0098FF, #0057D4)' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--text-muted)', borderTop: '1px solid var(--border-light)', paddingTop: 14, marginTop: 'auto' }}>
                    {t(ind.quote)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ROLE BASED ─────────────────────────────────────────────────── */}
      <section style={{ padding: 'var(--section-pad)', background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F7FF 100%)' }}>
        <div className="li-wrap">
          <div className="li-role-grid">
            <motion.div variants={slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ textAlign: 'center' }}>
              <img src={`${IMG}/role-manager.png`} alt={t('Location manager dashboard')} loading="lazy" decoding="async" style={{ width: '100%', maxWidth: 420, height: 'auto', display: 'inline-block' }} />
            </motion.div>
            <motion.div variants={slideInRight} initial="hidden" whileInView="show" viewport={viewportOnce}>
              <div className="li-eyebrow" style={{ marginBottom: 16 }}>{t('Role Based')}</div>
              <blockquote style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(20px, 2.3vw, 28px)', color: 'var(--text-heading)', lineHeight: 1.3, letterSpacing: '-0.01em', margin: '0 0 18px' }}>
                {t('“I know exactly which locations need attention, what customers are saying, and where to focus next.”')}
              </blockquote>
              <p style={{ fontSize: 16, color: 'var(--text-body)', lineHeight: 1.7, margin: '0 0 26px' }}>
                {t('Locobuzz helps location managers spend less time gathering data and more time improving customer experiences.')}
              </p>
              <div className="li-role-checks">
                {ROLE_CHECKS.map((c) => (
                  <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-subtle, #EAF6FF)', border: '1.5px solid #BFE3FF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check size={12} color="var(--brand)" strokeWidth={3} />
                    </span>
                    <span style={{ fontSize: 14.5, color: 'var(--text-secondary)' }}>{t(c)}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── BOOK A DEMO (standard site CTA) ────────────────────────────── */}
      <BookDemoSection />
    </>
  );
}
